import {
  GatewayAssujetti,
  GatewayConformite,
  GatewayEntreprise,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import { AssujettiWithAvancement } from "@gateway/assujetti/controller/assujetti.definition";
import {
  activitesFromEntreprise,
  isIas,
  isIobsp,
} from "@endya-conformite/ui-kit";
import { statusConfig } from "./ticket.helper";

export type AvancementStatus = "yes" | "maybe" | "no";

export const AVANCEMENT_STATUS_MAP = {
  yes: statusConfig("success", "Validé"),
  maybe: statusConfig("info", "En cours"),
  no: statusConfig("error", "Non valide"),
};

export const getAvancementStatus = (
  personne: AssujettiWithAvancement
): AvancementStatus => {
  const fields = [
    personne.avancement?.honorabilite,
    personne.avancement?.capaciteProfessionnelleIas,
    personne.avancement?.capaciteProfessionnelleIobsp,
    personne.avancement?.formationContinueIas,
    personne.avancement?.formationContinueIobsp,
  ].filter((val) => val !== null && val !== undefined);

  if (fields.length === 0) return "no";

  const completedCount = fields.filter((val) => val === 100).length;

  if (completedCount === fields.length) return "yes";
  if (completedCount > 0) return "maybe";
  return "no";
};

export function getAvailableAxes(
  entreprise?: Pick<
    GatewayEntreprise.EntrepriseAControllerResponseType,
    "categoriesActivites" | "categoriesActivitesAccessoires"
  >
): GatewayConformite.AxeConformite[] {
  const axes: GatewayConformite.AxeConformite[] = [
    GatewayConformite.AxeConformite.HONORABILITE,
  ];
  if (!entreprise) return axes;
  const categories = activitesFromEntreprise(entreprise);
  if (isIas(categories)) {
    axes.push(GatewayConformite.AxeConformite.CAPACITE_PRO_IAS);
    axes.push(GatewayConformite.AxeConformite.FORMATION_CONTINUE_IAS);
  }
  if (isIobsp(categories)) {
    axes.push(GatewayConformite.AxeConformite.CAPACITE_PRO_IOBSP);
    axes.push(GatewayConformite.AxeConformite.FORMATION_CONTINUE_IOBSP);
  }
  return axes;
}

type AvancementKey =
  keyof GatewayAssujetti.AssujettiWithAvancement["avancement"];

export const AXE_LABELS: Partial<
  Record<
    GatewayConformite.AxeConformite,
    { label: string; avancementKey: AvancementKey }
  >
> = {
  [GatewayConformite.AxeConformite.HONORABILITE]: {
    label: "Honorabilité",
    avancementKey: "honorabilite",
  },
  [GatewayConformite.AxeConformite.CAPACITE_PRO_IAS]: {
    label: "Capacité professionnelle IAS",
    avancementKey: "capaciteProfessionnelleIas",
  },
  [GatewayConformite.AxeConformite.CAPACITE_PRO_IOBSP]: {
    label: "Capacité professionnelle IOBSP",
    avancementKey: "capaciteProfessionnelleIobsp",
  },
  [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IAS]: {
    label: "Formation continue IAS",
    avancementKey: "formationContinueIas",
  },
  [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IOBSP]: {
    label: "Formation continue IOBSP",
    avancementKey: "formationContinueIobsp",
  },
};

export const axeConformiteToQuestionnaireSlug: Record<
  GatewayConformite.AxeConformiteAssujetti,
  GatewayQuestionnaire.QuestionnaireSlug
> = {
  [GatewayConformite.AxeConformite.HONORABILITE]: "cq_honorabilite",
  [GatewayConformite.AxeConformite.CAPACITE_PRO_IAS]: "cq_capacite_pro_ias",
  [GatewayConformite.AxeConformite.CAPACITE_PRO_IOBSP]: "cq_capacite_pro_iobsp",
  [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IAS]:
    "cq_formation_continue_ias",
  [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IOBSP]:
    "cq_formation_continue_iobsp",
};

export function getQuestionnaireSlug(
  axe: GatewayConformite.AxeConformite
): GatewayQuestionnaire.QuestionnaireSlug | undefined {
  if (
    axe === GatewayConformite.AxeConformite.HONORABILITE ||
    axe === GatewayConformite.AxeConformite.CAPACITE_PRO_IAS ||
    axe === GatewayConformite.AxeConformite.CAPACITE_PRO_IOBSP ||
    axe === GatewayConformite.AxeConformite.FORMATION_CONTINUE_IAS ||
    axe === GatewayConformite.AxeConformite.FORMATION_CONTINUE_IOBSP
  ) {
    return axeConformiteToQuestionnaireSlug[axe];
  }
  return undefined;
}
